import type {
  LineGeometry,
  LinePath,
  LinePoint,
  LineStepAnchor,
} from "@/pages/canvas/utils/linePath";
import {
  beginStepRunDrag,
  moveStepRun,
  snapBend,
  snapStepValue,
} from "@/pages/canvas/utils/linePath";
import { cn } from "@/utils";
import {
  Position,
  useReactFlow,
  useViewport,
  ViewportPortal,
  type XYPosition,
} from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LATTICE } from "../../canvas-controls.config";
import { CANVAS_FLOATING_TOOLBAR_Z_INDEX } from "../../constants/canvasFloatingToolbar.constants";

export interface EndpointDrop {
  nodeId: string;
  side: Position;
}

/** A side handle on a node an edge end can snap to. */
interface SnapTarget extends EndpointDrop, XYPosition {}

/** Screen px within which a dragged end snaps to a node's side handle. */
const SNAP_RADIUS_PX = 28;
/** Screen px within which a dragged run or bend lines up with a guide. */
const SNAP_TOLERANCE_PX = 8;
/** Off a guide, a drag settles on the half-lattice, never on a stray pixel. */
const SNAP_GRID = LATTICE / 2;
/** Screen px of travel before a press on a grip counts as a drag. */
const DRAG_THRESHOLD_PX = 3;

interface LineRouteHandlesProps {
  geometry: LineGeometry;
  built: LinePath;
  /**
   * Where the geometry's origin sits in flow space. Edges are in flow space
   * (0,0); a connector's geometry is local to its node.
   */
  origin?: XYPosition;
  /**
   * Double-clicking a grip is double-clicking the line under it. An edge
   * needs nothing here (the event reaches React Flow's edge handler); a
   * connector's handler sits on its path, so it passes one in.
   */
  onLineDoubleClick?: () => void;
  /** Curved: the through-point moved; `final` on release. */
  onBendChange?: (bend: XYPosition | null, final: boolean) => void;
  /** Elbow: a run slid; `final` on release. */
  onPointsChange?: (points: LinePoint[], final: boolean) => void;
  /** Edges only: an end is being dragged (null = released), in geometry space. */
  onEndpointDrag?: (end: "source" | "target", at: XYPosition | null) => void;
  /** Edges only: the end was dropped on a node's side (null = nowhere). */
  onEndpointDrop?: (
    end: "source" | "target",
    drop: EndpointDrop | null,
  ) => void;
}

const ORIGIN: XYPosition = { x: 0, y: 0 };

interface Drag {
  kind: "bend" | "anchor" | "end";
  pointerId: number;
  /** Where the press landed, to tell a click from a drag. */
  startClient: XYPosition;
  moved: boolean;
  /** Bend: the through-point relative to the pointer at the press. */
  offset?: XYPosition;
  anchor?: LineStepAnchor;
  base?: { points: LinePoint[]; first: number; last: number };
  end?: "source" | "target";
}

/**
 * The grips a selected line wears, drawn in a layer above the nodes so they
 * take the pointer even where they sit on a node's own connect handle:
 * a circle at the through-point of a curved line, a bar on each run of an
 * elbow, and, on an edge, a circle at each end for re-attaching it. Route
 * grips are filled and end grips hollow, so the two read apart. All hold
 * their size at any zoom, and every drag snaps to the guides that make a
 * tidy line.
 */
export function LineRouteHandles({
  geometry,
  built,
  origin = ORIGIN,
  onLineDoubleClick,
  onBendChange,
  onPointsChange,
  onEndpointDrag,
  onEndpointDrop,
}: LineRouteHandlesProps) {
  const { screenToFlowPosition, getInternalNode, getNodes } = useReactFlow();
  const { zoom } = useViewport();
  const [snap, setSnap] = useState<SnapTarget | null>(null);
  const dragRef = useRef<Drag | null>(null);
  // Ends the drag in flight, if any: the window listeners come off with it.
  const endDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endDragRef.current?.(), []);
  const scale = 1 / Math.max(zoom, 0.05);
  const tolerance = SNAP_TOLERANCE_PX * scale;

  const localAt = useCallback(
    (e: { clientX: number; clientY: number }): XYPosition => {
      const p = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      return { x: p.x - origin.x, y: p.y - origin.y };
    },
    [screenToFlowPosition, origin.x, origin.y],
  );

  const bendFrom = useCallback(
    (p: XYPosition): XYPosition => ({
      x: p.x - (geometry.source.x + geometry.target.x) / 2,
      y: p.y - (geometry.source.y + geometry.target.y) / 2,
    }),
    [
      geometry.source.x,
      geometry.source.y,
      geometry.target.x,
      geometry.target.y,
    ],
  );

  // The nearest side handle of any node within reach of the pointer. Only
  // a side the node actually renders a connect handle for counts: a drawing,
  // a connector or a comment marker has none, and an end written to a handle
  // that does not exist would leave the edge unresolvable.
  const snapTargetNear = useCallback(
    (flow: XYPosition): SnapTarget | null => {
      const radius = SNAP_RADIUS_PX * scale;
      let best: SnapTarget | null = null;
      let bestDist = radius;
      for (const node of getNodes()) {
        const internal = getInternalNode(node.id);
        const w = internal?.measured?.width;
        const h = internal?.measured?.height;
        const pos = internal?.internals.positionAbsolute;
        if (!internal || !w || !h || !pos || node.hidden) continue;
        const handles = internal.internals.handleBounds?.source ?? [];
        if (handles.length === 0) continue;
        const sides: [Position, XYPosition][] = [
          [Position.Top, { x: pos.x + w / 2, y: pos.y }],
          [Position.Right, { x: pos.x + w, y: pos.y + h / 2 }],
          [Position.Bottom, { x: pos.x + w / 2, y: pos.y + h }],
          [Position.Left, { x: pos.x, y: pos.y + h / 2 }],
        ];
        for (const [side, at] of sides) {
          if (!handles.some((handle) => handle.id === `${side}-source`)) {
            continue;
          }
          const d = Math.hypot(at.x - flow.x, at.y - flow.y);
          if (d < bestDist) {
            bestDist = d;
            best = { nodeId: node.id, side, ...at };
          }
        }
      }
      return best;
    },
    [getInternalNode, getNodes, scale],
  );

  // One routine for move and release: `final` is the only difference.
  const apply = (e: PointerEvent, d: Drag, final: boolean) => {
    const p = localAt(e);
    if (d.kind === "bend" && d.offset) {
      const through = { x: p.x + d.offset.x, y: p.y + d.offset.y };
      onBendChange?.(
        snapBend(geometry, bendFrom(through), tolerance, SNAP_GRID),
        final,
      );
    }
    if (d.kind === "anchor" && d.anchor && d.base) {
      const { points, first, last } = d.base;
      const value = snapStepValue(
        geometry,
        points,
        first,
        last,
        d.anchor.axis,
        p[d.anchor.axis],
        tolerance,
        SNAP_GRID,
      );
      onPointsChange?.(
        moveStepRun(points, first, last, d.anchor.axis, value),
        final,
      );
    }
    if (d.kind === "end" && d.end) {
      const target = snapTargetNear({ x: p.x + origin.x, y: p.y + origin.y });
      if (final) {
        setSnap(null);
        onEndpointDrag?.(d.end, null);
        onEndpointDrop?.(
          d.end,
          target ? { nodeId: target.nodeId, side: target.side } : null,
        );
      } else {
        setSnap(target);
        onEndpointDrag?.(
          d.end,
          target ? { x: target.x - origin.x, y: target.y - origin.y } : p,
        );
      }
    }
  };

  // A drag listens on the window rather than capturing the pointer on the
  // grip: the grips re-render as the line moves under them, and a grip that
  // remounts mid-drag would drop a captured pointer and never see the release.
  const start = (
    e: React.PointerEvent,
    drag: Omit<Drag, "startClient" | "moved">,
  ) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    endDragRef.current?.();
    const d: Drag = {
      ...drag,
      startClient: { x: e.clientX, y: e.clientY },
      moved: false,
    };
    dragRef.current = d;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== d.pointerId) return;
      if (!d.moved) {
        const travel = Math.hypot(
          ev.clientX - d.startClient.x,
          ev.clientY - d.startClient.y,
        );
        if (travel < DRAG_THRESHOLD_PX) return;
        d.moved = true;
      }
      apply(ev, d, false);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== d.pointerId) return;
      end();
      // A press that never travelled is a click: the line stays as it was.
      if (d.moved) apply(ev, d, true);
    };
    const end = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragRef.current = null;
      endDragRef.current = null;
    };
    endDragRef.current = end;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const place = (at: XYPosition, rotateDeg = 0): React.CSSProperties => ({
    position: "absolute",
    left: at.x + origin.x,
    top: at.y + origin.y,
    transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotateDeg}deg)`,
    pointerEvents: "auto",
  });

  const gripEvents = { onDoubleClick: onLineDoubleClick };

  const circle = (
    key: string,
    kind: string,
    at: XYPosition,
    onDown: (e: React.PointerEvent) => void,
    filled: boolean,
    onDoubleClick?: () => void,
  ) => (
    <div
      key={key}
      data-line-grip={kind}
      className={cn(
        "nodrag nopan size-3 rounded-full border-2 border-canvas-selection",
        filled ? "bg-canvas-selection" : "bg-n-00",
      )}
      style={{ ...place(at), cursor: "move" }}
      onPointerDown={onDown}
      {...gripEvents}
      onDoubleClick={
        onDoubleClick
          ? (e) => {
              e.stopPropagation();
              onDoubleClick();
            }
          : onLineDoubleClick
      }
    />
  );

  const bar = (a: LineStepAnchor) => (
    <div
      key={a.id}
      data-line-grip="anchor"
      data-axis={a.axis}
      className="nodrag nopan h-[5px] w-5 rounded-full border border-canvas-selection bg-canvas-selection"
      style={{
        // A bar lies along its run: vertical runs get a vertical bar.
        ...place(a, a.axis === "x" ? 90 : 0),
        cursor: a.axis === "x" ? "ew-resize" : "ns-resize",
      }}
      onPointerDown={(e) =>
        start(e, {
          kind: "anchor",
          pointerId: e.pointerId,
          anchor: a,
          base: beginStepRunDrag(geometry, built, a),
        })
      }
      {...gripEvents}
    />
  );

  return (
    <ViewportPortal>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          pointerEvents: "none",
          // Level with the toolbars, and so above a selected node (React
          // Flow lifts those to the same level): a connector's own wide hit
          // path must not take the press meant for its grips.
          zIndex: CANVAS_FLOATING_TOOLBAR_Z_INDEX,
        }}
      >
        {built.bendHandle &&
          onBendChange &&
          circle(
            "bend",
            "bend",
            built.bendHandle,
            (e) => {
              const p = localAt(e);
              const through = built.bendHandle!;
              start(e, {
                kind: "bend",
                pointerId: e.pointerId,
                offset: { x: through.x - p.x, y: through.y - p.y },
              });
            },
            true,
            geometry.bend ? () => onBendChange(null, true) : undefined,
          )}
        {onPointsChange && built.stepAnchors.map(bar)}
        {onEndpointDrop &&
          (["source", "target"] as const).map((end) =>
            circle(
              end,
              end,
              geometry[end],
              (e) => start(e, { kind: "end", pointerId: e.pointerId, end }),
              false,
            ),
          )}
        {snap && (
          <div
            data-line-grip="snap"
            className="nodrag nopan size-4 rounded-full border-2 border-canvas-selection bg-canvas-selection/40"
            style={{
              ...place({ x: snap.x - origin.x, y: snap.y - origin.y }),
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </ViewportPortal>
  );
}
